import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useLang } from "../i18n";
import { api } from "../api";
import PropertyCard from "../components/PropertyCard";

export default function Buy() {
  const { t } = useLang();
  const [properties, setProperties] = useState([]);
  const [filters, setFilters] = useState({ type: "", neigh: "", budget: "" });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/properties", { params: { status_filter: "for_sale", limit: 30 } });
        setProperties(res.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const neighborhoods = useMemo(() => {
    return Array.from(new Set(properties.map((p) => p.neighborhood))).sort();
  }, [properties]);

  const filtered = useMemo(() => {
    return properties.filter((p) => {
      if (filters.type && p.property_type !== filters.type) return false;
      if (filters.neigh && !p.neighborhood.toLowerCase().includes(filters.neigh.toLowerCase())) return false;
      if (filters.budget) {
        const max = parseInt(filters.budget, 10);
        if (max && p.price > max) return false;
      }
      return true;
    });
  }, [properties, filters]);

  return (
    <div data-testid="buy-page">
      <section className="relative min-h-[60vh] flex items-end pb-20 overflow-hidden grain">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=2000&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/50 to-[#0F0F0F]" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-12 w-full">
          <div className="eyebrow mb-5">{t.buy.eyebrow}</div>
          <h1 className="font-serif text-5xl md:text-7xl text-[#F5F5F0] tracking-tight leading-[0.95] max-w-4xl">
            {t.buy.title}
          </h1>
          <p className="text-white/60 mt-8 max-w-xl leading-relaxed">{t.buy.intro}</p>
        </div>
      </section>

      {/* SEARCH */}
      <section className="py-16 md:py-20 bg-[#0F0F0F] border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="bg-[#141414] border border-white/5 p-8 md:p-10" data-testid="property-search">
            <div className="flex items-center gap-3 mb-8">
              <MagnifyingGlass size={18} weight="thin" className="text-[#D4AF37]" />
              <span className="eyebrow">{t.buy.search_title}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <label className="text-[0.65rem] uppercase tracking-[0.25em] text-white/50 block mb-3">{t.buy.search_type}</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="input-line cursor-pointer"
                  data-testid="filter-type"
                >
                  <option value="" className="bg-white text-black">{t.buy.search_any}</option>
                  <option value="condo" className="bg-white text-black">{t.buy.types.condo}</option>
                  <option value="duplex" className="bg-white text-black">{t.buy.types.duplex}</option>
                  <option value="house" className="bg-white text-black">{t.buy.types.house}</option>
                  <option value="penthouse" className="bg-white text-black">{t.buy.types.penthouse}</option>
                </select>
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-[0.25em] text-white/50 block mb-3">{t.buy.search_neigh}</label>
                <input
                  type="text"
                  list="neigh-options"
                  value={filters.neigh}
                  onChange={(e) => setFilters({ ...filters, neigh: e.target.value })}
                  placeholder={t.buy.search_any}
                  className="input-line"
                  data-testid="filter-neigh"
                />
                <datalist id="neigh-options">
                  {neighborhoods.map((n) => <option key={n} value={n} />)}
                </datalist>
              </div>
              <div>
                <label className="text-[0.65rem] uppercase tracking-[0.25em] text-white/50 block mb-3">{t.buy.search_budget}</label>
                <select
                  value={filters.budget}
                  onChange={(e) => setFilters({ ...filters, budget: e.target.value })}
                  className="input-line cursor-pointer"
                  data-testid="filter-budget"
                >
                  <option value="" className="bg-white text-black">{t.buy.search_any}</option>
                  <option value="500000" className="bg-white text-black">≤ 500,000</option>
                  <option value="750000" className="bg-white text-black">≤ 750,000</option>
                  <option value="1000000" className="bg-white text-black">≤ 1,000,000</option>
                  <option value="1500000" className="bg-white text-black">≤ 1,500,000</option>
                  <option value="2000000" className="bg-white text-black">≤ 2,000,000</option>
                  <option value="3000000" className="bg-white text-black">≤ 3,000,000</option>
                  <option value="5000000" className="bg-white text-black">≤ 5,000,000</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="pb-28 md:pb-36 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {filtered.length === 0 ? (
            <div className="text-center py-24 text-white/50">—</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          )}
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0B0B0B] border-t border-white/5 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/contact" className="btn-gold" data-testid="buy-cta">{t.buy.cta}</Link>
        </div>
      </section>
    </div>
  );
}
